import React from 'react';
import { Box, Typography, Button, Alert, AlertTitle } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      errorId: Date.now().toString()
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Log to external service in production
    if (process.env.NODE_ENV === 'production') {
      this.logErrorToService(error, errorInfo);
    }
  }

  logErrorToService = (error, errorInfo) => {
    // In a real app, you would send this to your error reporting service
    // like Sentry, LogRocket, or Bugsnag
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: JSON.parse(localStorage.getItem('user') || '{}')._id
    };

    console.log('Error logged:', errorData);
    // Example: sendToErrorService(errorData);
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { fallback: Fallback, showDetails = false } = this.props;
      
      // If a custom fallback component is provided, use it
      if (Fallback) {
        return (
          <Fallback
            error={this.state.error}
            errorInfo={this.state.errorInfo}
            onRetry={this.handleRetry}
            onReload={this.handleReload}
          />
        );
      }

      // Default error UI
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          p={4}
          textAlign="center"
        >
          <ErrorOutlineIcon 
            sx={{ fontSize: 64, color: 'error.main', mb: 2 }} 
          />
          
          <Typography variant="h4" gutterBottom color="error">
            Oops! Something went wrong
          </Typography>
          
          <Typography variant="body1" color="text.secondary" mb={3} maxWidth="600px">
            We're sorry for the inconvenience. An unexpected error occurred while loading this page.
            Please try refreshing the page or contact support if the problem persists.
          </Typography>

          <Box display="flex" gap={2} mb={3}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={this.handleRetry}
              color="primary"
            >
              Try Again
            </Button>
            
            <Button
              variant="outlined"
              onClick={this.handleReload}
            >
              Reload Page
            </Button>
          </Box>

          {showDetails && this.state.error && (
            <Alert severity="error" sx={{ mt: 2, textAlign: 'left', maxWidth: '800px' }}>
              <AlertTitle>Error Details (Development Mode)</AlertTitle>
              <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </Typography>
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary" mt={2}>
            Error ID: {this.state.errorId}
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for wrapping components with error boundary
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Specialized error boundary for API errors
export const ApiErrorBoundary = ({ children, onError, fallback }) => {
  return (
    <ErrorBoundary
      fallback={fallback || ApiErrorFallback}
      onError={onError}
    >
      {children}
    </ErrorBoundary>
  );
};

// Default fallback for API errors
const ApiErrorFallback = ({ error, onRetry }) => (
  <Alert 
    severity="error" 
    action={
      <Button color="inherit" size="small" onClick={onRetry}>
        Retry
      </Button>
    }
  >
    <AlertTitle>Failed to load data</AlertTitle>
    {error?.message || 'An error occurred while fetching data. Please try again.'}
  </Alert>
);

export default ErrorBoundary;